import React, { useMemo, useState } from 'react';
import rules from './drugCalculatorRules.json';
import './drug-calculator.css';

const LB_PER_KG = 2.2046226218;

function formatNumber(value, digits = 2) {
  if (!Number.isFinite(value)) return '—';
  return Number(value).toFixed(digits).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
}

function formatMl(value) {
  if (!Number.isFinite(value)) return '—';
  return `${Number(value).toFixed(2)} mL`;
}

function medicationFor(indication, medicationId) {
  return indication?.medications?.find(item => item.id === medicationId) || indication?.medications?.[0] || null;
}

function ProtocolReference({ protocol, title, detail }) {
  return (
    <div className="drugcalc-protocol-ref">
      <strong>{protocol} · {title}</strong>
      {detail ? <span>{detail}</span> : null}
    </div>
  );
}

function PushDoseEpi({ onOpenProtocols }) {
  const epi = rules.pushDoseEpinephrine;
  return (
    <div className="drugcalc-stack">
      <section className="drugcalc-card drugcalc-card--hero">
        <span className="drugcalc-kicker">PUSH-DOSE EPINEPHRINE</span>
        <h3>Prepare 10 mcg/mL</h3>
        <div className="drugcalc-prep-grid">
          <div><small>START WITH</small><strong>{epi.sourceLabel}</strong><span>100 mcg/mL</span></div>
          <div><small>DRAW EPI</small><strong>{formatMl(epi.preparation.epinephrineMl)}</strong><span>from the 1:10,000 syringe</span></div>
          <div><small>ADD NS</small><strong>{formatMl(epi.preparation.salineMl)}</strong><span>normal saline</span></div>
          <div><small>FINAL</small><strong>{epi.preparation.totalMl} mL</strong><span>at {epi.finalConcentrationMcgMl} mcg/mL</span></div>
        </div>
        <p className="drugcalc-prep-instruction">{epi.preparation.instruction}</p>
      </section>

      <section className="drugcalc-card">
        <span className="drugcalc-kicker">ADMINISTRATION</span>
        <div className="drugcalc-big-result">
          <div><small>PROTOCOL RANGE</small><strong>{epi.doseMinMcg}-{epi.doseMaxMcg} mcg</strong></div>
          <div className="drugcalc-equals">=</div>
          <div><small>GIVE</small><strong>{epi.doseMinMl}-{epi.doseMaxMl} mL</strong></div>
        </div>
        <p><strong>{epi.interval}</strong>, titrated to the blood-pressure target in the applicable protocol.</p>
        <div className="drugcalc-dose-chips" aria-label="Push-dose epinephrine volume equivalents">
          <span>5 mcg = 0.50 mL</span>
          <span>10 mcg = 1.00 mL</span>
          <span>15 mcg = 1.50 mL</span>
          <span>20 mcg = 2.00 mL</span>
        </div>
      </section>

      <section className="drugcalc-card">
        <span className="drugcalc-kicker">CURRENT SAC COUNTY ADULT USES</span>
        <div className="drugcalc-use-list">
          {epi.uses.map(use => (
            <div className="drugcalc-use" key={use.protocol}>
              <ProtocolReference protocol={use.protocol} title={use.title} detail={use.context} />
              <p>{use.administration}</p>
            </div>
          ))}
        </div>
        {onOpenProtocols ? <button type="button" className="drugcalc-secondary" onClick={onOpenProtocols}>Open Sac County Protocols</button> : null}
      </section>
    </div>
  );
}

function WeightBasedResult({ indication, medication, weightLb }) {
  const concentration = rules.concentrations[medication.id];
  const kg = weightLb / LB_PER_KG;
  const uncappedDose = kg * medication.dosePerKg;
  const dose = Math.min(uncappedDose, medication.maxSingle);
  const capped = uncappedDose > medication.maxSingle;
  const volumeMl = dose / concentration.amountPerMl;
  const vials = Math.max(1, Math.ceil(volumeMl / concentration.vialVolumeMl));

  const cumulativeDose = medication.maxCumulativePerKg
    ? Math.min(kg * medication.maxCumulativePerKg, medication.maxCumulative)
    : null;
  const cumulativeMl = cumulativeDose != null ? cumulativeDose / concentration.amountPerMl : null;
  const maxDoseCourse = medication.maxDoses ? dose * medication.maxDoses : null;
  const maxVolumeCourse = medication.maxDoses ? volumeMl * medication.maxDoses : null;
  const isMidazolam = medication.id === 'midazolam';

  return (
    <div className="drugcalc-stack">
      <section className="drugcalc-card drugcalc-card--hero">
        <div className="drugcalc-result-head">
          <div><span className="drugcalc-kicker">{concentration.label.toUpperCase()}</span><h3>{indication.label}</h3></div>
          <span className="drugcalc-protocol-pill">{indication.protocol}</span>
        </div>
        <div className="drugcalc-weight-row"><span>{formatNumber(weightLb, 1)} lb</span><span>→</span><strong>{formatNumber(kg, 1)} kg</strong></div>
        <div className="drugcalc-result-grid">
          <div><small>PROTOCOL FORMULA</small><strong>{medication.dosePerKg} {medication.doseUnit}/kg</strong></div>
          <div><small>{isMidazolam ? 'PATIENT-SPECIFIC CEILING' : 'CALCULATED DOSE'}</small><strong>{formatNumber(dose)} {medication.doseUnit}</strong>{capped ? <span className="drugcalc-cap">Capped at protocol max {medication.maxSingle} {medication.doseUnit}</span> : null}</div>
          <div className="drugcalc-volume-result"><small>{isMidazolam ? 'DRAW UP TO' : 'DRAW / GIVE'}</small><strong>{formatMl(volumeMl)}</strong><span>{concentration.concentrationLabel} · {concentration.vialLabel}{vials > 1 ? ` · ${vials} vials needed for this volume` : ''}</span></div>
        </div>
      </section>

      {isMidazolam ? <section className="drugcalc-card"><span className="drugcalc-kicker">ROUTE-SPECIFIC VOLUME</span><div className="drugcalc-route-list"><div><strong>IV</strong><span>2 mg = 2.00 mL increments, slow IV push; titrate to effect and do not exceed {formatNumber(dose)} mg / {formatMl(volumeMl)}.</span></div><div><strong>IN</strong><span>{formatMl(volumeMl)} total; {formatMl(volumeMl / 2)} in each nostril.</span></div><div><strong>IM</strong><span>{formatMl(volumeMl)} total as the calculated single IM dose. May split between two sites if needed per protocol.</span></div></div></section> : null}

      {medication.id === 'fentanyl' ? <section className="drugcalc-card"><span className="drugcalc-kicker">FENTANYL LIMITS</span><div className="drugcalc-limit-grid"><div><small>SINGLE DOSE</small><strong>{formatNumber(dose)} mcg = {formatMl(volumeMl)}</strong><span>{medication.route} · {medication.repeat}</span></div><div><small>MAX CUMULATIVE FOR THIS WEIGHT</small><strong>{formatNumber(cumulativeDose)} mcg = {formatMl(cumulativeMl)}</strong><span>Never more than {medication.maxCumulative} mcg total under 8066.11.</span></div></div></section> : null}

      {medication.id === 'ketamine' ? <section className="drugcalc-card"><span className="drugcalc-kicker">KETAMINE ADMINISTRATION</span><p><strong>Draw {formatMl(volumeMl)} ({formatNumber(dose)} mg)</strong>, mix in 50-100 mL NS or D5W, and infuse over 10 minutes.</p><p>If pain remains or returns to moderate/severe, protocol allows one additional identical calculated dose.</p><div className="drugcalc-limit-grid"><div><small>EACH DOSE</small><strong>{formatNumber(dose)} mg = {formatMl(volumeMl)}</strong></div><div><small>TWO DOSES TOTAL, IF BOTH GIVEN</small><strong>{formatNumber(maxDoseCourse)} mg = {formatMl(maxVolumeCourse)}</strong></div></div></section> : null}

      <section className="drugcalc-card drugcalc-card--compact"><ProtocolReference protocol={indication.protocol} title={indication.protocolTitle} detail={`${medication.route} · ${medication.repeat}`} /><p>{medication.notes}</p></section>
    </div>
  );
}

export default function AdultDrugCalculator({ onOpenProtocols }) {
  const [indicationId, setIndicationId] = useState('pain');
  const indication = useMemo(() => rules.indications.find(item => item.id === indicationId) || null, [indicationId]);
  const [medicationId, setMedicationId] = useState('fentanyl');
  const [weight, setWeight] = useState('');

  const pushDose = indicationId === 'push-dose-epi';
  const medication = useMemo(() => medicationFor(indication, medicationId), [indication, medicationId]);
  const weightLb = Number(weight);
  const validWeight = Number.isFinite(weightLb) && weightLb > 0 && weightLb <= 1000;
  const reviewLocked = Date.now() >= Date.parse(`${rules.reviewBefore}T00:00:00-07:00`);

  const chooseIndication = value => {
    setIndicationId(value);
    const next = rules.indications.find(item => item.id === value);
    if (next?.medications?.length) setMedicationId(next.medications[0].id);
  };

  return (
    <div className="drugcalc-root">
      <div className="drugcalc-meta"><div><span className="drugcalc-kicker">OFFLINE · ADULT FIELD TOOL</span><h3>Adult Drug Calculator</h3><p>Calculates Sacramento County adult weight-based dosing, applies protocol maximums, and converts the final dose to the configured Line Medic carried concentration.</p></div><span className="drugcalc-offline">NO NETWORK REQUIRED</span></div>

      {reviewLocked ? <div className="drugcalc-review-lock"><strong>Protocol review required before calculator use.</strong><span>This ruleset was intentionally set to expire when Sacramento County's November 1, 2026 protocol changes take effect. Use the official Protocols library until the calculator rules are re-verified.</span>{onOpenProtocols ? <button type="button" onClick={onOpenProtocols}>Open Protocols</button> : null}</div> : <>
        <section className="drugcalc-controls">
          <label><span>Indication</span><select value={indicationId} onChange={event => chooseIndication(event.target.value)}><option value="pain">Pain Management</option><option value="seizure">Seizure</option><option value="behavioral">Behavioral Crisis / Restraint</option><option value="push-dose-epi">Push-Dose Epinephrine</option></select></label>
          {!pushDose && indication ? <label><span>Medication</span><select value={medication?.id || ''} onChange={event => setMedicationId(event.target.value)}>{indication.medications.map(item => <option key={item.id} value={item.id}>{rules.concentrations[item.id]?.label || item.id}</option>)}</select></label> : null}
          {!pushDose ? <label><span>Patient weight (lb)</span><input type="number" inputMode="decimal" min="1" max="1000" step="0.1" value={weight} onChange={event => setWeight(event.target.value)} placeholder="Enter weight in pounds" />{validWeight ? <small>{formatNumber(weightLb / LB_PER_KG, 1)} kg</small> : <small>Adult patients only · enter pounds</small>}</label> : null}
        </section>
        {pushDose ? <PushDoseEpi onOpenProtocols={onOpenProtocols} /> : null}
        {!pushDose && indication && medication && validWeight ? <WeightBasedResult indication={indication} medication={medication} weightLb={weightLb} /> : null}
        {!pushDose && !validWeight ? <div className="drugcalc-empty">Choose the indication and medication, then enter the patient's weight in pounds.</div> : null}
      </>}

      <div className="drugcalc-footer-note"><strong>Field calculation aid — confirm against the official protocol and medication label before administration.</strong><span>Concentrations: Fentanyl 50 mcg/mL · Midazolam 1 mg/mL · Ketamine 50 mg/mL. Current Sacramento County rules verified {rules.verifiedAt}; mandatory review before {rules.reviewBefore}.</span></div>
    </div>
  );
}
